// @flow
import * as React from 'react';
import StickySidebar from 'components/StickySidebar';
import { sprintf } from 'sprintf-js';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { toSemanticFilter } from 'utils/search/sidefilters';
import { isNotEmpty } from 'utils/functional';
import { getUserTimezone, getTimezoneFromLatLong } from 'utils/timezones';
import isPointGeotype from 'utils/search/isPointGeotype';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import LocationInputContainer from 'containers/LocationInputContainer';
import Container from 'components/Container';
import Text, { BodySmall, H5 } from 'components/Text';
import Button, { LinkButton } from 'components/Button';
import RadioToggle from 'components/RadioToggle';
import Link from 'components/Link';
import Box from 'components/Box';
import Pill from 'components/Pill';
import SearchRadiusSelector from 'components/SearchRadiusSelector';
import SearchFilterSection from 'components/SearchFilterSection';
import SearchFilterSectionList from 'components/SearchFilterSectionList';
import SearchFilterSectionDate from 'components/SearchFilterSectionDate';
import ButtonGroup, { Button as GroupButton } from 'components/ButtonGroup';
import { Label } from 'components/Form/Field';
import CloseIcon from 'close-thin.svg';
import FiltersIcon from 'filters.svg';
import type { GradSortType, SortType } from '../redux/main/reducers/mainSearch';
import {
  breakpoints,
  colors,
  gutterWidth,
  transition,
  fontWeights
} from '../theme';

const { useState } = React;

const StyledLabel = styled(Label)`
  & + * {
    display: block;
    margin-top: 6px !important;
  }
`;

const SortButtonContainer = styled.div`
  margin-bottom: 12px;
`;

const InactiveLinkButton = styled(LinkButton)`
  font-weight: normal;
  color: ${colors.battleshipGrey};
`;

const StyledCloseIcon = styled(CloseIcon)`
  color: ${colors.waterBlue};
  margin-left: auto;
  margin-top: auto;
  margin-bottom: auto;
`;

const StyledFiltersIcon = StyledCloseIcon.withComponent(FiltersIcon);

const ToggleFiltersContainer = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.waterBlue};
  border-top: solid 1px #c4c8cc;
  padding-top: 16px;
  margin-bottom: 16px;
  padding-left: ${gutterWidth / 2}px;
  padding-right: ${gutterWidth / 2}px;
  @media (min-width: ${breakpoints[2]}) {
    display: none;
  }
`;

const FiltersContainer = styled.div`
  display: none;
  &.shown {
    display: block;
  }
  @media (min-width: ${breakpoints[2]}) {
    display: block;
    .is-expanded + .is-expanded {
      margin-top: 12px;
    }
  }
`;

const ClearMobileContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  font-size: 18px;
  border-top: solid 1px #c4c8cc;
  padding-top: 16px;
  margin-bottom: 16px;
  padding-left: ${gutterWidth / 2}px;
  padding-right: ${gutterWidth / 2}px;

  a {
    display: flex;
    flex: 1;
  }

  @media (min-width: ${breakpoints[2]}) {
    display: none;
  }
`;

const ClearAndSortContainer = styled.div`
  display: none;
  align-items: center;
  height: 40px;
  width: 100%;
  @media (min-width: ${breakpoints[2]}) {
    display: flex;
    margin-bottom: 21px;
  }
`;

const StyledSearchFiltersSidebar = styled(Box)`
  width: 100%;
`;

const FilterPill = ({
  text,
  clearFilter
}: {|
  text: string,
  clearFilter: Function
|}) => (
  <Box py="4px" px="2px" maxWidth="50%">
    <Pill
      onClick={clearFilter}
      title={sprintf(getText(`Clear %(option)s filter`), {
        option: text
      })}
      text={text}
    />
  </Box>
);

const ExpandButton = styled(Box)`
  color: ${colors.waterBlue} !important;
  transition: all ${transition};
  &:focus,
  &:hover {
    background: ${colors.waterBlue};
    color: white !important;
  }
`;

const SelectedFilters = ({
  filters,
  facets,
  clearFilter,
  setFilter,
  selectAllSearchFilterOptions,
  searchLocation,
  setSearchLocation,
  searchLocationActive,
  ...props
}: {
  facets: Object,
  filters: Object,
  clearFilter: Function,
  setFilter: Function,
  selectAllSearchFilterOptions: Function,
  searchLocation: ?SearchLocation,
  setSearchLocation: Function,
  searchLocationActive: boolean
}) => {
  const [expanded, setExpanded] = useState(false);

  const pills = facets
    .map(facet => {
      const filter = filters[facet.id];
      if (Array.isArray(filter) && filter.length < facet.options.length) {
        if (facet.id === 'listingLocationTypeFacet') {
          if (
            ['ONSITE', 'REMOTE_TEMPORARY_COVID'].every(f => filter.includes(f))
          ) {
            return (
              <FilterPill
                key="onsite"
                text={getText('On-Site')}
                clearFilter={() => selectAllSearchFilterOptions(facet.id)}
              />
            );
          }
          if (
            ['REMOTE', 'REMOTE_TEMPORARY_COVID'].every(f => filter.includes(f))
          ) {
            return (
              <FilterPill
                key="remote"
                text={getText('Remote')}
                clearFilter={() => selectAllSearchFilterOptions(facet.id)}
              />
            );
          }
        }
        return filter.map(option => (
          <FilterPill
            key={option}
            text={facet.optionTitles[option]}
            clearFilter={() => clearFilter(facet.id, option)}
          />
        ));
      }
      if (facet.id === 'actionDateFacet' && typeof filter === 'object') {
        const timezone =
          searchLocationActive &&
          searchLocation &&
          searchLocation.latitude &&
          searchLocation.longitude
            ? getTimezoneFromLatLong(
                searchLocation.latitude,
                searchLocation.longitude
              )
            : getUserTimezone();

        return Object.keys(
          (filter: {
            starts: { value: ?(number | string | boolean) },
            ends: { value: ?(number | string | boolean) }
          })
        ).map(key =>
          filter[key] ? (
            <FilterPill
              key={key}
              text={`${
                key === 'starts' ? getText('After') : getText('Before')
              }: ${DateTime.fromMillis(filter[key].value * 1000, {
                timeZone: timezone
              }).toLocaleString({
                ...DateTime.DATE_SHORT,
                timeZone: timezone
              })}`}
              clearFilter={() =>
                setFilter(
                  facet.id,
                  toSemanticFilter({
                    startName: 'starts',
                    endName: 'ends',
                    startValue:
                      key === 'starts'
                        ? null
                        : filter.starts
                        ? filter.starts.value
                        : null,
                    endValue:
                      key === 'ends'
                        ? null
                        : filter.ends
                        ? filter.starts.value
                        : null
                  })
                )
              }
            />
          ) : null
        );
      }

      if (
        ['employmentTypeFacet', 'isCovidFacet'].includes(facet.id) &&
        filter !== 'ALL'
      ) {
        return (
          <FilterPill
            key={facet.id}
            text={facet.optionTitles[filter]}
            clearFilter={() => setFilter(facet.id, 'ALL')}
          />
        );
      }
      return null;
    })
    .reduce((acc, facet) => acc.concat(facet), [])
    .filter(pill => pill);

  const maxFilters = searchLocationActive ? 3 : 4;

  return (
    <Box display="flex" flexWrap="wrap" {...props}>
      {searchLocationActive && (
        <FilterPill
          key="location"
          text={
            searchLocation && isNotEmpty(searchLocation.text)
              ? sprintf(getText('Location: %(location)s'), {
                  location: searchLocation.text
                })
              : getText('Location')
          }
          clearFilter={() => setSearchLocation(null)}
        />
      )}

      {pills.filter((pill, index) => expanded || index < maxFilters)}

      {pills.length > maxFilters && !expanded && (
        <Box py="4px" px="2px">
          <ExpandButton
            component={Button}
            onClick={() => setExpanded(true)}
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={32}
            width={32}
            borderRadius="50%"
            boxShadow={`0 0 0 1px ${colors.waterBlue} inset`}
            letterSpacing="0.05em"
          >
            <Text styleAs="bodySmall" fontWeight={fontWeights.semiBold}>
              +{pills.length - maxFilters}
            </Text>
          </ExpandButton>
        </Box>
      )}
    </Box>
  );
};

const SearchFiltersSidebar = ({
  query,
  sort,
  searchLocation,
  searchRadius,
  filters,
  facetsToRender,
  facetsToRenderAsSelected,
  searchableFacets,
  sortOptionsToggle = [],
  sortOptionsNamed = [],
  defaultFiltersSet,
  setSearchFilter,
  selectSearchFilterOption,
  deselectSearchFilterOption,
  selectSearchFilterSpecificOptions,
  selectAllSearchFilterOptions,
  clearAllFilters,
  setSearchLocation,
  setSearchRadius,
  setSearchSort = () => null,
  isStaff,
  header,
  searchLocationActive,
  hideSearchLocation,
  ...props
}: {
  query: string,
  sort?: SortType | GradSortType,
  searchLocation?: ?SearchLocation,
  searchRadius?: string,
  filters: SearchFilters,
  facetsToRender: Array<SearchFacet>,
  facetsToRenderAsSelected?: Array<SearchFacet>,
  searchableFacets: Array<string>,
  sortOptionsNamed?: Array<{
    name: string,
    baseParam: string,
    defaultOrder: string
  }>,
  sortOptionsToggle?: Array<{ name: string, param: string }>,
  defaultFiltersSet: boolean,
  setSearchFilter: Function,
  selectSearchFilterOption: Function,
  selectSearchFilterSpecificOptions: Function,
  deselectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function,
  clearAllFilters: Function,
  setSearchLocation?: Function,
  setSearchRadius?: Function,
  setSearchSort: Function,
  isStaff?: boolean,
  searchLocationActive: boolean,
  hideSearchLocation?: boolean,
  header?: React.Node
}) => {
  const hasLocationType = facetsToRender.some(
    facet => facet.id === 'listingLocationTypeFacet'
  );

  const [currentSection, setCurrentSection] = useState(
    hasLocationType ? 'location' : null
  );

  const locationType = !hasLocationType
    ? null
    : Array.isArray(filters.listingLocationTypeFacet) &&
      !filters.listingLocationTypeFacet.includes('REMOTE')
    ? 'ONSITE'
    : Array.isArray(filters.listingLocationTypeFacet) &&
      !filters.listingLocationTypeFacet.includes('ONSITE')
    ? 'REMOTE'
    : 'ALL';

  const toggleCurrentSection = (index: any) => {
    setCurrentSection(index === currentSection ? null : index);
  };

  const showRadiusSection =
    searchLocation && isPointGeotype(searchLocation.geoType);
  const showToggleSort =
    query && setSearchSort && isNotEmpty(sortOptionsToggle);
  const showNameSort = setSearchSort && isNotEmpty(sortOptionsNamed);
  const clearFilters = () => {
    clearAllFilters();
    toggleCurrentSection(null);
  };

  return (
    <StickySidebar offsetTop={100} offsetBottom={10}>
      <Box {...props}>
        <Box pb={[gutterWidth / 2, null, null, 0]}>
          <ToggleHelperContainer>
            {(shown, toggleShown) => (
              <StyledSearchFiltersSidebar
                data-qa-id="search-filters"
                borderBottom={[`1px solid ${colors.silver}`, null, null, 0]}
              >
                {facetsToRender.length > 0 && (
                  <ToggleFiltersContainer
                    as="h2"
                    onClick={toggleShown}
                    data-qa-id="toggle-search-filters"
                  >
                    {shown ? (
                      <>
                        {getText('Hide Filters')}
                        <StyledCloseIcon width="19" height="20" />
                      </>
                    ) : (
                      <>
                        {getText('Filters')}
                        <StyledFiltersIcon width="22" height="19" />
                      </>
                    )}
                  </ToggleFiltersContainer>
                )}
                <FiltersContainer className={shown ? 'shown' : null}>
                  <ClearAndSortContainer>
                    <H5 mb={0}>
                      <Box display="flex" as="span">
                        {getText('Filters')}
                        {!defaultFiltersSet && (
                          <Link
                            ml={20}
                            href="#"
                            data-qa-id="clear-search-filters"
                            onClick={e => {
                              e.preventDefault();
                              clearFilters();
                            }}
                          >
                            {getText('Clear')}
                          </Link>
                        )}
                      </Box>
                    </H5>
                    {showToggleSort && (
                      <ButtonGroup ml="auto">
                        {sortOptionsToggle.map(option => (
                          <GroupButton
                            key={option.param}
                            onClick={() => setSearchSort(option.param)}
                            selected={sort === option.param}
                            data-qa-id={`select-order-${option.param}`}
                          >
                            {option.name}
                          </GroupButton>
                        ))}
                      </ButtonGroup>
                    )}
                  </ClearAndSortContainer>

                  {facetsToRender.length > 0 && (
                    <ClearMobileContainer>
                      {defaultFiltersSet ? (
                        <Text color="battleshipGrey">Clear</Text>
                      ) : (
                        <Link
                          href="#"
                          data-qa-id="clear-search-filters"
                          onClick={e => {
                            e.preventDefault();
                            clearFilters();
                          }}
                        >
                          {getText('Clear')}
                        </Link>
                      )}
                    </ClearMobileContainer>
                  )}
                  {!defaultFiltersSet && (
                    <SelectedFilters
                      mx={[0, null, null, '-6px']}
                      mt={[0, null, null, -16]}
                      pb={24}
                      width={['100%', null, null, 'calc(100% + 12px)']}
                      px={[gutterWidth / 2, null, null, 0]}
                      filters={filters}
                      facets={facetsToRenderAsSelected || facetsToRender}
                      clearFilter={deselectSearchFilterOption}
                      setFilter={setSearchFilter}
                      selectAllSearchFilterOptions={
                        selectAllSearchFilterOptions
                      }
                      searchLocation={searchLocation}
                      setSearchLocation={setSearchLocation}
                      searchLocationActive={searchLocationActive}
                      data-qa-id="selected-filters"
                    />
                  )}

                  {showNameSort && (
                    <Box px={[gutterWidth / 2, null, null, 0]}>
                      <BodySmall mb="0">{getText('Sort by')}</BodySmall>
                      <Box my="16px">
                        {sortOptionsNamed.map(option => (
                          <SortButtonContainer key={option.baseParam}>
                            {sort === `${option.baseParam}-asc` && (
                              <LinkButton
                                onClick={() =>
                                  setSearchSort(`${option.baseParam}-desc`)
                                }
                                data-qa-id={`sort-${option.baseParam}-asc`}
                              >
                                <BodySmall>{option.name} &darr;</BodySmall>
                              </LinkButton>
                            )}
                            {sort === `${option.baseParam}-desc` && (
                              <LinkButton
                                onClick={() =>
                                  setSearchSort(`${option.baseParam}-asc`)
                                }
                                data-qa-id={`sort-${option.baseParam}-desc`}
                              >
                                <BodySmall>{option.name} &uarr;</BodySmall>
                              </LinkButton>
                            )}
                            {sort !== `${option.baseParam}-asc` &&
                              sort !== `${option.baseParam}-desc` && (
                                <InactiveLinkButton
                                  onClick={() =>
                                    setSearchSort(
                                      `${option.baseParam}-${
                                        option.defaultOrder
                                      }`
                                    )
                                  }
                                  data-qa-id={`sort-${option.baseParam}`}
                                >
                                  <BodySmall>{option.name}</BodySmall>
                                </InactiveLinkButton>
                              )}
                          </SortButtonContainer>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {header}

                  {setSearchLocation && !hideSearchLocation && (
                    <SearchFilterSection
                      title={
                        hasLocationType
                          ? getText('Location & Remote Preferences')
                          : getText('Location')
                      }
                      data-qa-id="distance-section"
                      toggleExpanded={() => toggleCurrentSection('location')}
                      expanded={currentSection === 'location'}
                      noMask
                    >
                      <Container>
                        {hasLocationType && (
                          <Box my={24}>
                            <RadioToggle
                              value={locationType}
                              name="location-type"
                              buttons={[
                                {
                                  label: getText('All'),
                                  value: 'ALL'
                                },
                                {
                                  label: getText('On-Site'),
                                  value: 'ONSITE'
                                },
                                {
                                  label: getText('Remote'),
                                  value: 'REMOTE'
                                }
                              ]}
                              onChange={v => {
                                if (v === 'ONSITE') {
                                  selectSearchFilterSpecificOptions(
                                    'listingLocationTypeFacet',
                                    ['ONSITE', 'REMOTE_TEMPORARY_COVID']
                                  );
                                } else if (v === 'REMOTE') {
                                  selectSearchFilterSpecificOptions(
                                    'listingLocationTypeFacet',
                                    ['REMOTE', 'REMOTE_TEMPORARY_COVID']
                                  );
                                } else {
                                  selectAllSearchFilterOptions(
                                    'listingLocationTypeFacet'
                                  );
                                }
                                if (showRadiusSection && setSearchRadius) {
                                  if (
                                    v === 'REMOTE' &&
                                    searchRadius !== 'country'
                                  ) {
                                    setSearchRadius('country');
                                  } else if (
                                    v === 'ONSITE' &&
                                    searchRadius === 'country'
                                  ) {
                                    setSearchRadius('auto');
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}

                        <StyledLabel>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            {getText('Location')}
                            {searchLocationActive && (
                              <Box
                                data-qa-id="clear-location"
                                component={LinkButton}
                                fontSize="inherit !important"
                                onClick={() => setSearchLocation(null)}
                              >
                                {getText('Search Everywhere')}
                              </Box>
                            )}
                          </Box>
                        </StyledLabel>
                        <LocationInputContainer
                          placeholder={getText('Everywhere')}
                          locationText={searchLocation && searchLocation.text}
                          onChange={setSearchLocation}
                          showClearButton
                        />
                      </Container>

                      <Container px={0} mt={2}>
                        {showRadiusSection && setSearchRadius && searchRadius && (
                          <>
                            <StyledLabel>{getText('Radius')}</StyledLabel>
                            <SearchRadiusSelector
                              radius={searchRadius}
                              setSearchRadius={setSearchRadius}
                              includeCountry={
                                !!locationType && locationType !== 'ONSITE'
                              }
                            />
                          </>
                        )}
                      </Container>
                    </SearchFilterSection>
                  )}
                  {facetsToRender.map(facet =>
                    facet.type === 'date' ? (
                      <SearchFilterSectionDate
                        key={facet.id}
                        facet={facet}
                        filters={filters}
                        setSearchFilter={setSearchFilter}
                        toggleExpanded={() => toggleCurrentSection(facet.name)}
                        expanded={currentSection === facet.name}
                        searchLocation={searchLocation}
                        searchLocationActive={searchLocationActive}
                      />
                    ) : (
                      <SearchFilterSectionList
                        key={facet.id}
                        facet={facet}
                        filters={filters}
                        setSearchFilter={setSearchFilter}
                        selectSearchFilterOption={selectSearchFilterOption}
                        deselectSearchFilterOption={deselectSearchFilterOption}
                        selectAllSearchFilterOptions={
                          selectAllSearchFilterOptions
                        }
                        hasOptionFilter={searchableFacets.includes(facet.id)}
                        toggleExpanded={() => toggleCurrentSection(facet.name)}
                        expanded={currentSection === facet.name}
                      />
                    )
                  )}
                  <ToggleFiltersContainer
                    as="h2"
                    mt={0}
                    onClick={toggleShown}
                    data-qa-id="toggle-search-filters"
                  >
                    {getText('Hide Filters')}
                    <StyledCloseIcon width="19" height="20" />
                  </ToggleFiltersContainer>
                </FiltersContainer>
              </StyledSearchFiltersSidebar>
            )}
          </ToggleHelperContainer>
          {showToggleSort && (
            <ButtonGroup
              mx="auto"
              mt={16}
              px="16px"
              display={['flex', 'flex', 'flex', 'none']}
              maxWidth="375px"
            >
              {sortOptionsToggle.map(option => (
                <GroupButton
                  key={option.param}
                  onClick={() => setSearchSort(option.param)}
                  selected={sort === option.param}
                  data-qa-id={`select-order-${option.param}`}
                  width="50%"
                  p="6px"
                  mb="10px"
                >
                  {option.name}
                </GroupButton>
              ))}
            </ButtonGroup>
          )}
        </Box>
      </Box>
    </StickySidebar>
  );
};

export default SearchFiltersSidebar;
